// src/modules/myvault/components/canvasBuilder/elements/ActionButtonElement.tsx

// What? Runtime renderer for the `action_button` canvas element. Shared by
//       both CanvasRenderer.ElementContent (production runtime) and the
//       ElementRenderer switch so we don't fork behaviour.
// Why?  The same button must render identically when a canvas is viewed
//       directly, embedded in a parent canvas, or in an older code path.
// How?  Pulls workspace/canvas/row context from CanvasRuntimeContext and
//       fires `/api/workspace/{ws}/myvault/canvases/{canvas_id?}/trigger-action`
//       via authFetch. Confirmation dialog is inline (matches existing
//       design), not a Joy modal, so it inherits grid cell sizing.

import React from "react";
import * as MuiIcons from "@mui/icons-material";
import { Box, Button, Sheet, Typography } from "@mui/joy";
import { useLocation, useNavigate } from "react-router-dom";
import type {
  CanvasElementProps,
  CanvasNavigationAction,
} from "../../../types";
import { useAuthFetch } from "../../../../../utils/useAuthFetch";
import { withEmbeddingPath } from "../../../services/viewService";
import { useCanvasRuntime } from "../CanvasRuntimeContext";
import { openCanvasNavigation } from "../canvasNavigation";

interface ActionButtonElementProps {
  props: CanvasElementProps;
  isPreview: boolean;
  readOnly?: boolean;
  elementId: string;
}

type ActionColor = "primary" | "neutral" | "success" | "warning" | "danger";
type ActionVariant = "solid" | "outlined" | "soft";

const LEGACY_STYLE_TO_COLOR: Record<string, ActionColor> = {
  primary: "primary",
  secondary: "neutral",
  danger: "danger",
};

export function ActionButtonElement({
  props,
  isPreview,
  readOnly = false,
  elementId,
}: ActionButtonElementProps) {
  const runtime = useCanvasRuntime();
  const authFetch = useAuthFetch();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = React.useState(false);
  const [feedback, setFeedback] = React.useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [showConfirm, setShowConfirm] = React.useState(false);

  const label = (props.label as string) || "Action";
  const icon = props.icon as string | undefined;
  const variant = ((props.actionVariant as string) || "solid") as ActionVariant;
  const configuredColor = props.actionColor as ActionColor | undefined;
  const legacyStyle = (props.style as string) || "primary";
  const color: ActionColor =
    configuredColor ?? LEGACY_STYLE_TO_COLOR[legacyStyle] ?? "primary";

  const confirmation = props.confirmation as
    { enabled?: boolean; message?: string; title?: string } | undefined;
  const confirmEnabled =
    confirmation?.enabled ?? (props.confirm_before_run as boolean | undefined);
  const isNavigation =
    (props.direct_action as CanvasNavigationAction | undefined)?.type ===
    "NAVIGATE";
  const disabled = isPreview || (readOnly && !isNavigation);

  const IconComponent = icon
    ? (MuiIcons as Record<string, React.ComponentType<{ fontSize?: string }>>)[
        icon.charAt(0).toUpperCase() +
          icon.slice(1).replace(/_([a-z])/g, (_, c: string) => c.toUpperCase())
      ]
    : undefined;

  const handleClick = () => {
    if (disabled) return;
    if (confirmEnabled) {
      setShowConfirm(true);
    } else {
      void executeAction();
    }
  };

  const executeAction = async () => {
    if (disabled) return;
    setShowConfirm(false);
    setFeedback(null);

    if (!runtime?.workspaceId) {
      setFeedback({
        type: "error",
        message: "Action buttons require a workspace context.",
      });
      setTimeout(() => setFeedback(null), 4000);
      return;
    }

    setLoading(true);
    try {
      const directAction = props.direct_action as
        CanvasNavigationAction | undefined;
      if (directAction?.type === "NAVIGATE") {
        const selections = Array.from(
          new Set(
            Object.values(runtime.interactionBus.selectedRows || {})
              .filter((row) => row.canvasId === runtime.canvasId)
              .map((row) => row.rowId),
          ),
        );
        await openCanvasNavigation(directAction.config, {
          workspaceId: runtime.workspaceId,
          currentRowId: runtime.rowId,
          selectedRowId: selections.length === 1 ? selections[0] : null,
          returnPath: location.pathname + location.search,
          authFetch,
          navigate,
        });
        return;
      }
      // The myvault router is mounted at `/api/workspace/myvault/...` with
      // the workspace id carried via the `x-workspace-id` header (authFetch
      // handles that upstream). Don't embed the workspace id in the path.
      if (!runtime.canvasId)
        throw new Error("Action buttons require a canvas context.");
      const path = withEmbeddingPath(
        `/api/workspace/myvault/canvases/${runtime.canvasId}/trigger-action`,
        runtime.embeddingPath,
      );
      const result = await authFetch(path, "trigger action button", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-workspace-id": runtime.workspaceId,
        },
        body: JSON.stringify({
          element_id: elementId,
          row_id: runtime.rowId ?? undefined,
          row_data: runtime.rowData ?? undefined,
        }),
      });
      setFeedback({
        type: result?.success ? "success" : "error",
        message:
          result?.message || (result?.success ? "Done" : "Failed to run"),
      });
    } catch (err) {
      setFeedback({
        type: "error",
        message:
          err instanceof Error
            ? err.message
            : "Destination unavailable or access denied.",
      });
    } finally {
      setLoading(false);
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Button
        color={color}
        variant={variant}
        size="sm"
        loading={loading}
        disabled={disabled}
        onClick={handleClick}
        startDecorator={
          IconComponent ? <IconComponent fontSize="small" /> : undefined
        }
      >
        {label}
      </Button>
      {feedback && (
        <Typography
          level="body-xs"
          sx={{
            color: feedback.type === "success" ? "success.600" : "danger.600",
          }}
        >
          {feedback.message}
        </Typography>
      )}
      {showConfirm && (
        <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: "sm", mt: 0.5 }}>
          <Typography level="body-sm" sx={{ mb: 1 }}>
            {confirmation?.message || "Are you sure?"}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="sm"
              color={color}
              variant={variant}
              onClick={() => {
                void executeAction();
              }}
              loading={loading}
            >
              Confirm
            </Button>
            <Button
              size="sm"
              variant="plain"
              onClick={() => setShowConfirm(false)}
            >
              Cancel
            </Button>
          </Box>
        </Sheet>
      )}
    </Box>
  );
}

export default ActionButtonElement;
